// ============================================================================
// LOCAL SUPABASE ACCESS-TOKEN VERIFICATION (JWKS — no network round-trip)
// ============================================================================
//
// CANONICAL, framework-agnostic implementation shared by every WTC app/package.
//
// PURPOSE: Verify a Supabase-issued access token (JWT) locally against the
// project's published JWKS, instead of calling `auth.getUser(token)` over the
// network (`https://<project>.supabase.co/auth/v1/user`).
//
// WHY: `getUser(token)` sits on the hot path of EVERY authenticated request.
// When GoTrue is slow/unavailable it fails with `AuthRetryableFetchError` /
// `UND_ERR_HEADERS_TIMEOUT`, returning 401 on otherwise-valid requests. A
// Supabase access token is a stateless, short-lived JWT (~1h): verifying its
// signature + `iss`/`aud`/`exp` locally is the SAME check `getUser` performs
// for an access token — there is no revocation state to lose — but with no
// per-request network dependency.
//
// GO-FORWARD PATTERN (see the SUPABASE_JWT_LOCAL_VERIFICATION engineering rule):
// server code that verifies a Bearer access token MUST try this local path
// first and fall back to the network `getUser(token)` only when this returns
// "unavailable" (symmetric/legacy token) or "failed" (rotation/JWKS fetch).
// That keeps behavior a strict superset of the old network-only path.
//
// The JWKS is fetched once and cached in-memory by `jose` across warm
// invocations (with rotation + cooldown handling), so the common case makes
// ZERO network calls. Unlike `/auth/v1/user`, the JWKS endpoint is static and
// cacheable, so even a cold fetch is far cheaper and more reliable.
// ============================================================================
import { createRemoteJWKSet, jwtVerify, errors as joseErrors } from "jose";
/**
 * Cache the JWKS remote set per JWKS URL. `createRemoteJWKSet` returns a function
 * that internally caches fetched keys (honoring `cacheMaxAge` + `cooldownDuration`),
 * so a single warm process verifies many tokens without re-fetching.
 */
let jwksCache = null;
function resolveSupabaseUrl(explicit) {
    const url = explicit ?? process.env.NEXT_PUBLIC_SUPABASE_URL;
    return url ? url.replace(/\/+$/, "") : null;
}
function getJwks(supabaseUrl) {
    const jwksUrl = `${supabaseUrl}/auth/v1/.well-known/jwks.json`;
    if (!jwksCache || jwksCache.url !== jwksUrl) {
        jwksCache = {
            url: jwksUrl,
            jwks: createRemoteJWKSet(new URL(jwksUrl), {
                // Re-fetch keys at most every 10 minutes; on an unknown `kid` jose
                // refetches once, rate-limited by cooldownDuration (handles rotation).
                cacheMaxAge: 10 * 60 * 1000,
                cooldownDuration: 30 * 1000,
            }),
        };
    }
    return jwksCache.jwks;
}
/** Read the JOSE header `alg` WITHOUT verifying. Null if unparseable. */
function readAlg(token) {
    const first = token.split(".", 1)[0];
    if (!first)
        return null;
    try {
        const json = Buffer.from(first.replace(/-/g, "+").replace(/_/g, "/"), "base64").toString("utf8");
        const header = JSON.parse(json);
        return typeof header.alg === "string" ? header.alg : null;
    }
    catch {
        return null;
    }
}
/** True for asymmetric algorithms verifiable via the public JWKS. */
export function isAsymmetricAlg(alg) {
    return (!!alg &&
        (alg.startsWith("ES") ||
            alg.startsWith("RS") ||
            alg.startsWith("PS") ||
            alg.startsWith("Ed")));
}
/**
 * Verify a Supabase access token locally against the project JWKS. Never throws
 * — every failure mode is reported as an outcome so the caller can decide
 * whether to fall back to the network `getUser(token)` path.
 */
export async function verifyAccessTokenLocally(token, options = {}) {
    const trimmed = token?.trim();
    if (!trimmed)
        return { status: "unavailable", reason: "empty token" };
    const supabaseUrl = resolveSupabaseUrl(options.supabaseUrl);
    if (!supabaseUrl)
        return { status: "unavailable", reason: "no supabase url" };
    const alg = readAlg(trimmed);
    // Symmetric (HS256) hand-minted/legacy tokens can't be verified via the public
    // JWKS; defer to the network path (which validates them via the legacy secret).
    if (!isAsymmetricAlg(alg)) {
        return { status: "unavailable", reason: `symmetric or unknown alg: ${alg ?? "none"}` };
    }
    try {
        const { payload } = await jwtVerify(trimmed, getJwks(supabaseUrl), {
            issuer: `${supabaseUrl}/auth/v1`,
            audience: options.audience ?? "authenticated",
        });
        const sub = typeof payload.sub === "string" ? payload.sub : null;
        if (!sub)
            return { status: "failed", reason: "missing sub claim" };
        const email = typeof payload.email === "string" ? payload.email : null;
        return { status: "verified", claims: { sub, email } };
    }
    catch (error) {
        if (error instanceof joseErrors.JWTExpired)
            return { status: "expired" };
        const reason = error instanceof Error
            ? error.code ?? error.message
            : String(error);
        // Signature mismatch, no-matching-key (rotation), or JWKS fetch failure.
        // Not a hard failure: the caller falls back to the network path.
        return { status: "failed", reason: String(reason) };
    }
}
